/**
 * SocialPanel - Activity feed panel
 *
 * Phase 5F: Migrated to React Query with useSocialFeed hook
 *
 * Features:
 * - React Query as single source of truth (no local state cache)
 * - Cache-first with background revalidation
 * - Bounded persistence for first page only
 * - Realtime updates via Supabase postgres_changes (in useSocialFeed)
 * - Infinite scroll with IntersectionObserver
 */

import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Users, ChevronDown, Loader2 } from 'lucide-react';
import { ActivityCard } from '../ActivityCard';
import { useSocialFeed } from '../../hooks/useSocialFeed';
import { ActivityCardSkeleton } from '../Skeleton';
import { useAuth } from '../../contexts/AuthContext';
import { effectiveTier } from '../../utils/entitlements';
import { UpgradeCTA } from '../ui/UpgradeCTA';
import type { FeedFilter } from '../../types/activity';

interface SocialPanelProps {
  onClose: () => void;
}

const FILTER_OPTIONS: { value: FeedFilter; label: string }[] = [
  { value: 'all', label: 'All Activity' },
  { value: 'rated', label: 'Ratings' },
  { value: 'added_to_watchlist', label: 'Added to Watchlist' },
  { value: 'added_to_watched', label: 'Watched' },
  { value: 'added_to_favorites', label: 'Added to Favorites' },
  { value: 'created_list', label: 'Created Lists' },
  { value: 'followed', label: 'Following' },
];

export function SocialPanel({ onClose }: SocialPanelProps) {
  const { profile } = useAuth();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef<HTMLDivElement>(null);

  // React Query hook - single source of truth for social feed
  const {
    activities,
    isLoading,
    isFetching,
    isFetchingNextPage,
    error,
    hasMore,
    loadMore,
  } = useSocialFeed({ filter, limit: 20, enabled: !isFree });

  // Phase 5F: Show cached data while fetching (no "Loading..." if cached exists)
  const showLoadingSkeleton = isLoading && activities.length === 0;

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isFetchingNextPage) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoading, isFetchingNextPage, loadMore]);

  // Close panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const activeFilterLabel = FILTER_OPTIONS.find((o) => o.value === filter)?.label || 'All Activity';

  return (
    <div className="h-full flex flex-col bg-[#0F1419]">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between p-4 border-b border-white/[0.06]">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-white">Social</h2>
          {/* Phase 5F: Subtle refresh indicator when background fetching */}
          {isFetching && !showLoadingSkeleton && (
            <Loader2 size={14} className="animate-spin text-slate-500" />
          )}
        </div>
      </div>

      {/* Filter Dropdown */}
      <div className="shrink-0 p-4 pb-2">
        <div className="relative">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center justify-between w-full px-3 py-2 bg-[#1A1F26] rounded-lg text-sm text-white hover:bg-slate-700 transition-colors"
          >
            <span>{activeFilterLabel}</span>
            <ChevronDown
              size={16}
              className={`text-slate-400 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown Menu */}
          {isFilterOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A1F26] rounded-lg ring-1 ring-white/[0.08] py-1 z-10 shadow-xl">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFilter(option.value);
                    setIsFilterOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                    filter === option.value
                      ? 'text-teal-400 bg-teal-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pt-2">
        {/* Upgrade CTA for Free users */}
        {isFree ? (
          <div className="flex items-center justify-center h-full">
            <UpgradeCTA feature="The social feed" onNavigate={onClose} />
          </div>
        ) : (
        <>
        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <p className="text-sm text-red-400 mb-4">{error}</p>
          </div>
        )}

        {/* Activities - show cached data while fetching */}
        {!error && activities.length > 0 && (
          <div className="space-y-3">
            {activities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}

            {/* Loading more indicator */}
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 size={24} className="animate-spin text-teal-400" />
              </div>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && <div ref={sentinelRef} className="h-1" />}
          </div>
        )}

        {/* Empty State */}
        {!error && !showLoadingSkeleton && activities.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <Users size={48} className="text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No activity yet</h3>
            <p className="text-sm text-slate-400">
              Follow friends to see what they're watching!
            </p>
          </div>
        )}

        {/* Initial Loading State - only show when no cached data */}
        {showLoadingSkeleton && (
          <div className="space-y-4 p-4">
            {[...Array(4)].map((_, i) => (
              <ActivityCardSkeleton key={i} />
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
